import React, { useState } from 'react';
import { Box, Container, Typography, Button, Stack, Grid, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { styled } from '@mui/material/styles';
import useMetaTags from '../../hooks/useMetaTags';
import Footer from '../../components/Footer/Footer';
import Header from '../../components/Header/Header';
import { Link } from 'react-router-dom';
import NatureIcon from '@mui/icons-material/Nature';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import GroupsIcon from '@mui/icons-material/Groups';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import GrassIcon from '@mui/icons-material/Grass';

// Hero Section Styles
const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '550px',
  overflow: 'hidden',
  background: 'linear-gradient(135deg, #f5f0e8 0%, #e8e0d5 100%)',
  marginTop: '100px',
  [theme.breakpoints.down('md')]: {
    marginTop: '80px',
    height: 'auto',
    minHeight: '500px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing(2, 2),
    overflow: 'hidden',
  },
  [theme.breakpoints.down('sm')]: {
    marginTop: '60px',
    padding: theme.spacing(1, 2),
    minHeight: '400px',
  }
}));

const HeroContent = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-100%, -50%)',
  zIndex: 2,
  maxWidth: '450px',
  paddingRight: '40px',
  [theme.breakpoints.down('md')]: {
    position: 'relative',
    left: 'auto',
    top: 'auto',
    transform: 'none',
    flex: '1',
    textAlign: 'left',
    padding: theme.spacing(2, 1),
    paddingRight: theme.spacing(1),
    order: 1,
  },
}));

const HeroTitle = styled(Typography)(({ theme }) => ({
  fontSize: '3.2rem',
  fontWeight: 700,
  color: '#2a3a28',
  marginBottom: theme.spacing(2),
  lineHeight: 1.1,
  fontFamily: '"Lora", serif',
  [theme.breakpoints.down('lg')]: {
    fontSize: '2.8rem',
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '2.5rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '2rem',
  }
}));

const HeroSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  fontWeight: 500,
  color: '#85b47c',
  marginBottom: theme.spacing(2),
  fontFamily: '"Lora", serif',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('md')]: {
    fontSize: '0.9rem',
    whiteSpace: 'normal',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8rem',
  }
}));

const HeroDescription = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  color: '#2a3a28',
  marginBottom: theme.spacing(3),
  maxWidth: '350px',
  lineHeight: 1.5,
  fontFamily: '"Lora", serif',
  [theme.breakpoints.down('md')]: {
    fontSize: '1rem',
    maxWidth: '100%',
    marginBottom: theme.spacing(2),
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
  }
}));

const HeroImageWrapper = styled(Box)(({ theme }) => ({
  position: 'absolute',
  right: '0',
  top: '50%',
  transform: 'translateY(-50%)',
  width: '800px',
  height: '800px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1,
  [theme.breakpoints.down('lg')]: {
    width: '700px',
    height: '700px',
  },
  [theme.breakpoints.down('md')]: {
    position: 'relative',
    right: 'auto',
    top: 'auto',
    transform: 'none',
    width: '100%',
    height: 'auto',
    order: 2,
    padding: theme.spacing(2),
    display: 'flex',
  }
}));

const HeroImageContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translateY(-50%)',
  width: '750px',
  height: '750px',
  zIndex: 1,
  [theme.breakpoints.down('md')]: {
    position: 'relative',
    left: 'auto',
    top: 'auto',
    transform: 'none',
    flex: '0 0 50%',
    maxWidth: 'none',
    width: '50%',
    height: 'auto',
    minHeight: '450px',
    order: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    overflow: 'visible',
  },
  [theme.breakpoints.down('sm')]: {
    flex: '0 0 50%',
    maxWidth: 'none',
    width: '50%',
    minHeight: '400px',
  }
}));

const IconFeatureBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  background: '#455b40',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  '& svg': {
    color: '#FFFFFF',
    fontSize: '28px',
  }
}));

// Content Section Styles
const ContentSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  background: '#FFFFFF',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '2.5rem',
  fontWeight: 700,
  color: '#2a3a28',
  marginBottom: theme.spacing(6),
  textAlign: 'center',
  fontFamily: '"Lora", serif',
  [theme.breakpoints.down('md')]: {
    fontSize: '2rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.75rem',
  }
}));

const FeatureCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: '20px',
  background: '#f5f0e8',
  height: '300px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  textAlign: 'center',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 12px 40px rgba(69, 91, 64, 0.15)',
  }
}));

const PurchaseCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(5),
  borderRadius: '20px',
  background: '#f5f0e8',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  border: '2px solid #85b47c',
}));

const PriceTag = styled(Typography)(({ theme }) => ({
  fontSize: '3rem',
  fontWeight: 700,
  color: '#455b40',
  marginBottom: theme.spacing(2),
  fontFamily: '"Lora", serif',
}));

const CTAButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#455b40',
  color: '#FFFFFF',
  padding: '14px 32px',
  borderRadius: '30px',
  fontSize: '1.1rem',
  fontWeight: 600,
  fontFamily: '"Lora", serif',
  textTransform: 'none',
  border: '2px solid #455b40',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#FFFFFF',
    color: '#455b40',
    borderColor: '#455b40',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
    padding: '12px 24px',
  }
}));

const MapSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  background: 'linear-gradient(135deg, #f5f0e8 0%, #e8e0d5 100%)',
}));

const PhilosophySection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(15, 0),
  position: 'relative',
  backgroundImage: 'url(/paisaje-ekko1.jpg)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundAttachment: 'fixed',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(42, 58, 40, 0.3)',
  }
}));

const PhilosophyContent = styled(Container)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  textAlign: 'center',
  color: '#FFFFFF',
}));

const FAQSectionContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#f8f8f8',
  padding: '80px 0',
  [theme.breakpoints.down('md')]: {
    padding: '50px 0'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '40px 0'
  }
}));

const FAQTitle = styled(Typography)(({ theme }) => ({
  color: '#B4A082',
  fontWeight: 400,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  fontSize: '0.9rem',
  marginBottom: '24px',
  textAlign: 'center',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.75rem',
    marginBottom: '16px'
  }
}));

const FAQHeading = styled(Typography)(({ theme }) => ({
  color: '#2c2c2c',
  fontWeight: 300,
  lineHeight: 1.2,
  marginBottom: '60px',
  fontSize: '2.5rem',
  letterSpacing: '1px',
  fontFamily: '"Lora", serif',
  textAlign: 'center',
  [theme.breakpoints.down('md')]: {
    fontSize: '2rem',
    marginBottom: '50px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.5rem',
    marginBottom: '35px'
  }
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  backgroundColor: 'transparent',
  marginBottom: '16px',
  borderRadius: '0 !important',
  boxShadow: 'none',
  border: 'none',
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: '0 0 16px 0',
  },
  [theme.breakpoints.down('sm')]: {
    marginBottom: '12px',
    '&.Mui-expanded': {
      margin: '0 0 12px 0',
    }
  }
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  backgroundColor: '#6c8c68',
  minHeight: '50px',
  padding: '0 24px',
  borderRadius: '25px',
  marginBottom: '8px',
  boxShadow: '0 4px 12px rgba(108, 140, 104, 0.2)',
  transition: 'all 0.3s ease',
  '&.Mui-expanded': {
    backgroundColor: '#455b40',
    boxShadow: '0 6px 20px rgba(69, 91, 64, 0.3)',
  },
  '&:hover': {
    backgroundColor: '#5a7862',
  },
  '& .MuiAccordionSummary-content': {
    margin: '12px 0',
    alignItems: 'center',
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    color: 'white',
    transition: 'transform 0.3s ease',
    '&.Mui-expanded': {
      transform: 'rotate(180deg)',
    },
  },
  [theme.breakpoints.down('sm')]: {
    minHeight: '44px',
    padding: '0 18px',
    borderRadius: '22px',
    '& .MuiAccordionSummary-content': {
      margin: '10px 0',
    }
  }
}));

const QuestionText = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  fontWeight: 600,
  color: 'white',
  fontFamily: '"Lora", serif',
  lineHeight: 1.4,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.95rem'
  }
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: '24px',
  backgroundColor: 'white',
  marginTop: '0',
  marginBottom: '8px',
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  border: '1px solid #f0f0f0',
  [theme.breakpoints.down('sm')]: {
    padding: '18px',
    borderRadius: '10px'
  }
}));

const AnswerText = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  color: '#666',
  lineHeight: 1.7,
  fontFamily: '"Lora", serif',
  whiteSpace: 'pre-line',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.85rem',
    lineHeight: 1.6
  }
}));

const faqData = [
  {
    question: "¿Qué variedad de granadas vendéis?",
    answer: "Vendemos la variedad Acco, de sabor dulce y color intenso, ideales para zumos, postres o comer al natural."
  },
  {
    question: "¿Dónde puedo comprar las granadas?",
    answer: "Puedes comprarlas directamente en <a href='https://maps.app.goo.gl/nkzQunMEWfyhrKcw8' target='_blank' rel='noopener noreferrer' style='color: #6c8c68; font-weight: 600; text-decoration: underline;'>nuestra finca Ekkofood en Bétera (Valencia)</a> o pedirlas a domicilio en toda la península."
  },
  {
    question: "¿Cuál es el precio?",
    answer: "En finca: 3,5 € / kilo.\n\nA domicilio: 4 € / kilo + gastos de envío (4–7 € según zona).\nPedido mínimo a domicilio: 5 kg."
  },
  {
    question: "¿Cuánto tiempo se conservan las granadas ecológicas?",
    answer: "Las granadas enteras pueden mantenerse en buen estado durante varias semanas: hasta 1 mes en un lugar fresco y seco, y entre 4 y 6 semanas si las guardas en la nevera.\nUna vez abiertas, los granos se conservan en la nevera dentro de un recipiente hermético durante 5 a 7 días."
  },
  {
    question: "¿Hacéis envíos fuera de España?",
    answer: "Por ahora solo realizamos envíos dentro de la península."
  },
  {
    question: "¿Las granadas tienen certificación ecológica?",
    answer: "Sí, nuestra finca cuenta con más de 20 años de experiencia en agricultura ecológica certificada."
  },
  {
    question: "¿Cuánto tiempo tardan en llegar los pedidos a domicilio?",
    answer: "El envío suele tardar entre 24 y 48 horas desde la cosecha para garantizar frescura."
  },
  {
    question: "¿Puedo visitar la finca?",
    answer: "¡Claro! Además de la venta directa, organizamos visitas y cursos sobre agricultura ecológica."
  }
];

const Granadas: React.FC = () => {
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  useMetaTags({
    title: 'Granadas Ecológicas - Directas desde nuestra finca',
    description: 'Compra granadas ecológicas directamente de nuestra finca en Valencia. Cultivo 100% ecológico, frescura garantizada, sabor intenso y natural.',
    image: 'https://ekkofood.com/granadas-design.png',
    url: 'https://ekkofood.com/granadas-ecologicas',
    type: 'website'
  });

  return (
    <>
      <Header />

      {/* Hero Section */}
      <HeroSection>
        {/* Text Content - Left */}
        <HeroContent>
          <HeroSubtitle component="p">DIRECTAS DESDE NUESTRA FINCA</HeroSubtitle>
          <HeroTitle component="h1">
            GRANADAS<br />
            ECOLÓGICAS
          </HeroTitle>
          <HeroDescription component="p">
            Compra granadas ecológicas<br />
            directamente de nuestra finca<br />
            en Valencia!
          </HeroDescription>

          <Stack direction="row" spacing={{ xs: 3, sm: 2, md: 2 }} sx={{ mt: { xs: 1.5, md: 3 }, justifyContent: { xs: 'flex-start', md: 'flex-start' } }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: { xs: 0.5, md: 1 }, flex: '1 1 0', minWidth: 0 }}>
                  <Box sx={{
                    width: { xs: '32px', sm: '40px', md: '50px' },
                    height: { xs: '32px', sm: '40px', md: '50px' },
                    borderRadius: '50%',
                    background: '#455b40',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <GrassIcon sx={{ color: '#FFFFFF', fontSize: { xs: '16px', sm: '18px', md: '24px' } }} />
                  </Box>
                  <Box sx={{ textAlign: 'center', width: '100%' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.65rem' }, color: '#2a3a28', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                      CULTIVO 100%
                    </Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.65rem' }, color: '#2a3a28', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                      ECOLÓGICO
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: { xs: 0.5, md: 1 }, flex: '1 1 0', minWidth: 0 }}>
                  <Box sx={{
                    width: { xs: '32px', sm: '40px', md: '50px' },
                    height: { xs: '32px', sm: '40px', md: '50px' },
                    borderRadius: '50%',
                    background: '#455b40',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <GrassIcon sx={{ color: '#FFFFFF', fontSize: { xs: '16px', sm: '18px', md: '24px' } }} />
                  </Box>
                  <Box sx={{ textAlign: 'center', width: '100%' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.65rem' }, color: '#2a3a28', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                      DIRECTAS
                    </Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.65rem' }, color: '#2a3a28', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                      DEL ÁRBOL
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: { xs: 0.5, md: 1 }, flex: '1 1 0', minWidth: 0 }}>
                  <Box sx={{
                    width: { xs: '32px', sm: '40px', md: '50px' },
                    height: { xs: '32px', sm: '40px', md: '50px' },
                    borderRadius: '50%',
                    background: '#455b40',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <GrassIcon sx={{ color: '#FFFFFF', fontSize: { xs: '16px', sm: '18px', md: '24px' } }} />
                  </Box>
                  <Box sx={{ textAlign: 'center', width: '100%' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.65rem' }, color: '#2a3a28', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                      SABOR
                    </Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.65rem' }, color: '#2a3a28', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                      INTENSO
                    </Typography>
                  </Box>
                </Box>
          </Stack>
        </HeroContent>

        {/* Image with Circle - Right */}
        <HeroImageContainer>
          <Box
            sx={{
              position: 'relative',
              width: { xs: 'auto', md: '100%' },
              height: { xs: 'auto', md: '100%' },
              minHeight: { xs: '400px', md: 'auto' },
              minWidth: { xs: '400px', md: 'auto' },
              display: { xs: 'flex', md: 'block' },
              alignItems: { xs: 'center', md: 'initial' },
              justifyContent: { xs: 'flex-start', md: 'initial' },
            }}
          >
            <Box
              component="svg"
              viewBox="0 0 900 900"
              xmlns="http://www.w3.org/2000/svg"
              sx={{
                position: { xs: 'absolute', md: 'absolute' },
                width: { xs: '320px', sm: '360px', md: '900px' },
                height: { xs: '320px', sm: '360px', md: '900px' },
                zIndex: 0,
                right: { xs: 'auto', md: 'auto' },
                left: { xs: '-20px', md: 'auto' },
                top: { xs: '50%', md: 'auto' },
                transform: { xs: 'translateY(-50%)', md: 'none' },
              }}
            >
              <circle
                cx="450"
                cy="450"
                r="430"
                fill="#455b40"
                style={{ display: 'block' }}
              />
            </Box>
            <Box
              component="img"
              src="/granadas-hero.png"
              alt="Granadas ecológicas frescas"
              sx={{
                position: { xs: 'absolute', md: 'absolute' },
                right: { xs: 'auto', md: 'auto' },
                left: { xs: '-110px', md: '-100px' },
                top: { xs: '50%', md: '50%' },
                transform: { xs: 'translateY(-50%)', md: 'translateY(-50%)' },
                width: { xs: '380px', sm: '420px', md: '1050px' },
                height: { xs: '380px', sm: '420px', md: '1050px' },
                objectFit: 'contain',
                zIndex: 1,
              }}
            />
          </Box>
        </HeroImageContainer>
      </HeroSection>

      {/* Cómo comprar */}
      <ContentSection sx={{ background: '#f5f0e8' }}>
        <Box sx={{
          maxWidth: '1100px',
          margin: '0 auto',
          px: { xs: 2, md: 4 }
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 0
          }}>
            {/* Text Section - 65% width */}
            <Box sx={{
              flex: { md: '0 0 65%' },
              py: 6,
              px: { xs: 2, md: 6 },
              background: '#FFFFFF',
              borderRadius: { xs: '20px', md: '20px 0 0 20px' },
              minHeight: '510px',
              position: 'relative'
            }}>
              <Typography component="h3" sx={{
                fontSize: '0.8rem',
                fontWeight: 600,
                color: '#85b47c',
                mb: 1.5,
                letterSpacing: '0.1em'
              }}>
                CÓMO COMPRAR
              </Typography>

              <Typography component="h2" sx={{
                fontSize: { xs: '1.8rem', md: '2.5rem' },
                fontWeight: 700,
                color: '#2a3a28',
                lineHeight: 1.2,
                fontFamily: '"Lora", serif',
                mb: 2.5
              }}>
                GRANADAS<br />
                DIRECTAMENTE<br />
                EN LA FINCA
              </Typography>

              {/* Organic Badge */}
              <Box sx={{
                position: 'absolute',
                right: { md: '40px' },
                top: { xs: '20px', md: '40px' },
                width: '80px',
                height: '80px',
                display: { xs: 'none', md: 'flex' }
              }}>
                <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="50" cy="50" r="48" fill="none" stroke="#85b47c" strokeWidth="2" strokeDasharray="3 3"/>
                  <text x="50" y="45" textAnchor="middle" fontSize="14" fill="#85b47c" fontWeight="600">ORGANIC</text>
                  <text x="50" y="60" textAnchor="middle" fontSize="10" fill="#85b47c">🌿</text>
                </svg>
              </Box>

              <Stack spacing={2} sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: '#85b47c',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Typography sx={{ color: '#FFFFFF', fontSize: '20px', fontWeight: 700 }}>✓</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '1rem', color: '#2a3a28' }}>
                    Precio: 3,5€/kg
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: '#85b47c',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Typography sx={{ color: '#FFFFFF', fontSize: '20px', fontWeight: 700 }}>✓</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '1rem', color: '#2a3a28' }}>
                    Recoge tus propias granadas<br />directamente del árbol
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: '#85b47c',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Typography sx={{ color: '#FFFFFF', fontSize: '20px', fontWeight: 700 }}>✓</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '1rem', color: '#2a3a28' }}>
                    Compras de más de 10 kg → vaso de<br />zumo gratis
                  </Typography>
                </Box>
              </Stack>

              <CTAButton
                sx={{ px: 4 }}
                component="a"
                href="https://calendly.com/ekkofood/granadas"
                target="_blank"
              >
                PROGRAMA TU VISITA
              </CTAButton>
            </Box>

            {/* Image Section - 35% width */}
            <Box sx={{
              flex: { md: '0 0 35%' },
              position: 'relative',
              borderRadius: { xs: '20px', md: '0 20px 20px 0' },
              overflow: 'hidden',
              minHeight: { xs: '400px', md: '513px' }
            }}>
              <img
                src="/granadas-comprar.png"
                alt="Mujer en la finca de granadas"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </Box>
          </Box>
        </Box>
      </ContentSection>

      {/* Granadas a domicilio */}
      <ContentSection sx={{ background: '#f5f0e8', pt: 0 }}>
        <Box sx={{
          maxWidth: '1100px',
          margin: '0 auto',
          px: { xs: 2, md: 4 }
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 0
          }}>
            {/* Image Section - 35% width */}
            <Box sx={{
              flex: { md: '0 0 35%' },
              position: 'relative',
              borderRadius: { xs: '20px', md: '20px 0 0 20px' },
              overflow: 'hidden',
              minHeight: { xs: '300px', md: '400px' }
            }}>
              <img
                src="/granadas-domicilio.png"
                alt="Granadas en caja de madera"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </Box>

            {/* Text Section - 65% width */}
            <Box sx={{
              flex: { md: '0 0 65%' },
              py: 6,
              px: { xs: 2, md: 6 },
              background: '#FFFFFF',
              borderRadius: { xs: '20px', md: '0 20px 20px 0' },
              minHeight: '400px',
              position: 'relative'
            }}>
              <Typography component="h3" sx={{
                fontSize: '0.8rem',
                fontWeight: 600,
                color: '#85b47c',
                mb: 1.5,
                letterSpacing: '0.1em',
                textAlign: 'right'
              }}>
                CÓMO COMPRAR
              </Typography>

              <Typography component="h2" sx={{
                fontSize: { xs: '1.5rem', md: '2.5rem' },
                fontWeight: 700,
                color: '#2a3a28',
                lineHeight: 1.2,
                fontFamily: '"Lora", serif',
                mb: 2.5,
                textAlign: 'right'
              }}>
                GRANADAS<br />
                A DOMICILIO
              </Typography>

              {/* Organic Badge */}
              <Box sx={{
                position: 'absolute',
                left: { md: '40px' },
                top: { xs: '20px', md: '40px' },
                width: '80px',
                height: '80px',
                display: { xs: 'none', md: 'flex' }
              }}>
                <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="50" cy="50" r="48" fill="none" stroke="#85b47c" strokeWidth="2" strokeDasharray="3 3"/>
                  <text x="50" y="45" textAnchor="middle" fontSize="14" fill="#85b47c" fontWeight="600">ORGANIC</text>
                  <text x="50" y="60" textAnchor="middle" fontSize="10" fill="#85b47c">🌿</text>
                </svg>
              </Box>

              <Stack spacing={2} sx={{ mb: 4, alignItems: 'flex-end' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexDirection: 'row-reverse' }}>
                  <Box sx={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: '#85b47c',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Typography sx={{ color: '#FFFFFF', fontSize: '20px', fontWeight: 700 }}>✓</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '1rem', color: '#2a3a28', textAlign: 'right' }}>
                    Precio: 4€/kg + envío (4–7€)
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexDirection: 'row-reverse' }}>
                  <Box sx={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: '#85b47c',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Typography sx={{ color: '#FFFFFF', fontSize: '20px', fontWeight: 700 }}>✓</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '1rem', color: '#2a3a28', textAlign: 'right' }}>
                    Pedido mínimo: 5 kg
                  </Typography>
                </Box>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ justifyContent: 'flex-end' }}>
                <CTAButton
                  sx={{ px: 3, whiteSpace: 'nowrap', fontSize: { xs: '0.85rem', md: '1rem' } }}
                  component="a"
                  href="https://wa.me/34629201793?text=Hola,%20quiero%20hacer%20un%20pedido%20de%20granadas%20a%20domicilio"
                  target="_blank"
                >
                  Haz tu pedido por WhatsApp
                </CTAButton>
                <CTAButton
                  sx={{ px: 3, whiteSpace: 'nowrap', fontSize: { xs: '0.85rem', md: '1rem' } }}
                  component={Link}
                  to="/contacto"
                >
                  Haz tu pedido por formulario
                </CTAButton>
              </Stack>
            </Box>
          </Box>
        </Box>
      </ContentSection>

      {/* Localización y contacto */}
      <MapSection>
        <Container maxWidth="xl" sx={{ px: { xs: 2, md: 4 } }}>
          <SectionTitle component="h2">Localización y contacto</SectionTitle>
          <Box sx={{
            background: '#FFFFFF',
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' }
          }}>
            <Box sx={{
              width: { xs: '100%', md: '33.333%' },
              p: 6,
              display: 'flex',
              alignItems: 'center',
              minHeight: '320px'
            }}>
              <Stack spacing={3}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <LocationOnIcon sx={{ color: '#455b40', fontSize: 32, mt: 0.5 }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#2a3a28', mb: 1 }}>
                      Dirección
                    </Typography>
                    <Typography sx={{ color: '#666666' }}>
                      C/ Les Llomes s/n. Bétera. 46117<br />
                      Valencia, España
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <PhoneIcon sx={{ color: '#455b40', fontSize: 32, mt: 0.5 }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#2a3a28', mb: 1 }}>
                      Teléfono y WhatsApp
                    </Typography>
                    <Typography sx={{ color: '#666666' }}>
                      + 34 629 201 793
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Box>
            <Box sx={{ width: { xs: '100%', md: '66.667%' } }}>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3078.9856778547!2d-0.45742899999999995!3d39.6369221!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd605d4f1e506a4d%3A0x769ac3c79e4b470!2sEkkofood!5e0!3m2!1sen!2ses!4v1703123456789"
                width="100%"
                height="320"
                style={{ border: 0, display: 'block' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Mapa de ubicación"
              />
            </Box>
          </Box>
        </Container>
      </MapSection>

      {/* Nuestra filosofía */}
      <PhilosophySection>
        <PhilosophyContent maxWidth="md">
          <Typography component="h2" variant="h3" sx={{
            fontWeight: 700,
            mb: 4,
            fontSize: { xs: '2rem', md: '3rem' },
            color: '#FFFFFF'
          }}>
            Nuestra filosofía
          </Typography>
          <Typography sx={{
            fontSize: '1.3rem',
            lineHeight: 1.8,
            mb: 4,
            color: '#FFFFFF'
          }}>
            "En Ekkofood nos apasiona acercar el campo a tu mesa, ofreciendo productos frescos,
            ecológicos y llenos de sabor."
          </Typography>
          <CTAButton
            component={Link}
            to="/quienes-somos"
            sx={{
              mt: 2,
              backgroundColor: '#FFFFFF',
              color: '#455b40',
              border: '2px solid #FFFFFF',
              '&:hover': {
                backgroundColor: 'transparent',
                color: '#FFFFFF',
                borderColor: '#FFFFFF',
              }
            }}
          >
            Conócenos más
          </CTAButton>
        </PhilosophyContent>
      </PhilosophySection>

      {/* FAQ Section */}
      <FAQSectionContainer>
        <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <FAQTitle component="h3">Preguntas Frecuentes</FAQTitle>
          <FAQHeading component="h2">Preguntas frecuentes sobre Granadas Ecológicas</FAQHeading>

          <Box>
            {faqData.map((faq, index) => (
              <StyledAccordion
                key={index}
                expanded={expanded === `panel${index}`}
                onChange={handleChange(`panel${index}`)}
              >
                <StyledAccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`panel${index}-content`}
                  id={`panel${index}-header`}
                >
                  <QuestionText>{faq.question}</QuestionText>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                  <AnswerText dangerouslySetInnerHTML={{ __html: faq.answer }} />
                </StyledAccordionDetails>
              </StyledAccordion>
            ))}
          </Box>
        </Container>
      </FAQSectionContainer>

      <Footer />
    </>
  );
};

export default Granadas;
